/**
 * RealFirstJobGuidedFlow
 *
 * One-flow, multi-step modal that guides a brand-new tenant through
 * creating their FIRST real customer + job + visit, all without dumping
 * them into the regular app between steps.
 *
 * Steps:
 *   0  intro          — "Great — now let's create your real first job."
 *   1  customer       — name / phone / email / service address
 *   2  job            — title / service type / description
 *   3  schedule       — date + start time (1h default)
 *   4  done           — "Great — your first real job is scheduled."
 *
 * Calls (all already exist):
 *   POST /api/customers
 *   POST /api/jobs
 *   POST /api/visits
 *
 * Does NOT touch FVO seed-cleanup / tagging — that already happened in
 * /api/first-value-onboarding/start-fresh before this modal is rendered.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { toast } from 'sonner';
import {
  User,
  MapPin,
  Briefcase,
  Calendar as CalendarIcon,
  CheckCircle,
  ArrowRight,
  Sparkles,
} from 'lucide-react';
import {
  normalizeEmail,
  isValidEmail,
  getEmailError,
  normalizeUsPhone,
  isValidUsPhone,
  getPhoneError,
} from '../utils/contactValidation';
import { useServiceTypes } from '../hooks/useServiceTypes';
import { useFirstValueOnboarding } from '../contexts/FirstValueOnboardingContext';
import { clearFvoSessionFlag } from '../contexts/FirstValueOnboardingContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const STEP_INTRO = 0;
const STEP_CUSTOMER = 1;
const STEP_JOB = 2;
const STEP_SCHEDULE = 3;
const STEP_DONE = 4;

const STEP_LABELS = ['Intro', 'Customer', 'Job', 'Schedule', 'Done'];

function StepDots({ current }) {
  return (
    <div className="flex items-center justify-center gap-2 pb-3" data-testid="fvo-realjob-steps">
      {STEP_LABELS.slice(1, 4).map((label, idx) => {
        const stepNum = idx + 1;
        const reached = current >= stepNum;
        const active = current === stepNum;
        return (
          <div key={label} className="flex items-center gap-2">
            <div
              className={
                'flex items-center justify-center w-7 h-7 rounded-full text-xs font-semibold border-2 ' +
                (active
                  ? 'bg-orange-600 text-white border-orange-600'
                  : reached
                    ? 'bg-orange-100 text-orange-700 border-orange-300'
                    : 'bg-zinc-100 text-zinc-500 border-zinc-200')
              }
            >
              {reached && !active ? '✓' : stepNum}
            </div>
            <span
              className={
                'text-xs font-medium hidden sm:inline ' +
                (active ? 'text-zinc-900' : reached ? 'text-zinc-700' : 'text-zinc-400')
              }
            >
              {label}
            </span>
            {idx < 2 && <div className="w-6 h-px bg-zinc-200 hidden sm:block" />}
          </div>
        );
      })}
    </div>
  );
}

const initialCustomer = {
  name: '',
  phone: '',
  email: '',
  street: '',
  address_line2: '',
  city: '',
  state: '',
  zip: '',
};

const initialJob = {
  title: '',
  service_type: '',
  description: '',
};

function todayPlusDaysISO(days = 1) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

const initialSchedule = {
  date: todayPlusDaysISO(1),
  start_time: '10:00',
  duration_minutes: 60,
};

export default function RealFirstJobGuidedFlow({ isOpen, onClose, onComplete }) {
  const { serviceTypes, getDefaultServiceType } = useServiceTypes();
  const { fetchStatus } = useFirstValueOnboarding();

  const [step, setStep] = useState(STEP_INTRO);
  const [submitting, setSubmitting] = useState(false);
  const [customer, setCustomer] = useState(initialCustomer);
  const [job, setJob] = useState(initialJob);
  const [schedule, setSchedule] = useState(initialSchedule);
  const [created, setCreated] = useState({ customerId: null, jobId: null, visitId: null });
  const [errors, setErrors] = useState({});

  // Default service type once loaded
  useEffect(() => {
    if (!job.service_type && serviceTypes.length > 0) {
      const def = getDefaultServiceType?.();
      if (def) setJob((j) => ({ ...j, service_type: def }));
    }
  }, [serviceTypes, getDefaultServiceType, job.service_type]);

  // Reset on close
  useEffect(() => {
    if (!isOpen) {
      setStep(STEP_INTRO);
      setSubmitting(false);
      setCustomer(initialCustomer);
      setJob(initialJob);
      setSchedule(initialSchedule);
      setCreated({ customerId: null, jobId: null, visitId: null });
      setErrors({});
    }
  }, [isOpen]);

  const headerTitle = useMemo(() => {
    if (step === STEP_INTRO) return "Let's create your first real job";
    if (step === STEP_DONE) return "You're up and running";
    return 'Create Your First Real Job';
  }, [step]);

  // ---- Validation helpers ----
  const validateCustomer = () => {
    const e = {};
    if (!customer.name.trim()) e.name = 'Customer name is required';
    if (!customer.phone.trim() && !customer.email.trim()) e.contact = 'Phone or email is required';
    if (customer.phone.trim() && !isValidUsPhone(customer.phone.trim())) {
      e.phone = getPhoneError(customer.phone.trim()) || 'Enter a 10-digit US phone';
    }
    const ne = normalizeEmail(customer.email);
    if (ne && !isValidEmail(ne)) e.email = getEmailError(ne) || 'Enter a valid email';
    if (!customer.street.trim()) e.street = 'Street is required';
    if (!customer.city.trim()) e.city = 'City is required';
    if (!customer.state.trim()) e.state = 'State is required';
    if (!customer.zip.trim()) e.zip = 'ZIP is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const validateJob = () => {
    const e = {};
    if (!job.title.trim()) e.title = 'Job title is required';
    if (!job.service_type) e.service_type = 'Pick a service type';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const validateSchedule = () => {
    const e = {};
    if (!schedule.date) e.date = 'Pick a date';
    if (!schedule.start_time) e.start_time = 'Pick a start time';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // ---- API calls ----
  const createCustomerApi = async () => {
    const body = {
      name: customer.name.trim(),
      phone: normalizeUsPhone(customer.phone.trim()) || null,
      email: normalizeEmail(customer.email) || null,
      preferred_communication: 'email',
      addresses: [
        {
          label: 'Primary',
          street: customer.street.trim(),
          address_line2: customer.address_line2.trim(),
          city: customer.city.trim(),
          state: customer.state.trim(),
          zip: customer.zip.trim(),
          is_primary: true,
        },
      ],
    };
    const res = await fetch(`${API}/customers`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Failed to create customer');
    }
    return res.json();
  };

  const createJobApi = async (customerId) => {
    const res = await fetch(`${API}/jobs`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        customer_id: customerId,
        title: job.title.trim(),
        description: job.description.trim() || '',
        service_type: job.service_type,
        status: 'draft',
        priority: 'normal',
      }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Failed to create job');
    }
    return res.json();
  };

  const createVisitApi = async (jobId) => {
    // Build local datetime → ISO (browser tz)
    const startLocal = new Date(`${schedule.date}T${schedule.start_time}:00`);
    const endLocal = new Date(startLocal.getTime() + (schedule.duration_minutes || 60) * 60 * 1000);
    const res = await fetch(`${API}/visits`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        job_id: jobId,
        scheduled_start: startLocal.toISOString(),
        scheduled_end: endLocal.toISOString(),
        assigned_technicians: [],
      }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Failed to schedule visit');
    }
    return res.json();
  };

  // ---- Step handlers ----
  const handleIntroContinue = () => setStep(STEP_CUSTOMER);

  const handleCustomerNext = async () => {
    if (!validateCustomer()) {
      toast.error('Please complete the required fields');
      return;
    }
    setSubmitting(true);
    try {
      const c = await createCustomerApi();
      setCreated((p) => ({ ...p, customerId: c.customer_id }));
      // Clear FVO session flag now that the user has created a real customer.
      // Verification rules will apply on subsequent fresh sessions.
      clearFvoSessionFlag();
      toast.success('Customer saved');
      setStep(STEP_JOB);
    } catch (err) {
      toast.error(err.message || 'Failed to create customer');
    } finally {
      setSubmitting(false);
    }
  };

  const handleJobNext = async () => {
    if (!validateJob()) {
      toast.error('Please complete the required fields');
      return;
    }
    if (!created.customerId) {
      toast.error('Customer not saved yet');
      setStep(STEP_CUSTOMER);
      return;
    }
    setSubmitting(true);
    try {
      const j = await createJobApi(created.customerId);
      setCreated((p) => ({ ...p, jobId: j.job_id }));
      toast.success('Nice — your real job is created. Now let\'s schedule the visit.');
      setStep(STEP_SCHEDULE);
    } catch (err) {
      toast.error(err.message || 'Failed to create job');
    } finally {
      setSubmitting(false);
    }
  };

  const handleScheduleNext = async () => {
    if (!validateSchedule()) {
      toast.error('Pick a date and time');
      return;
    }
    if (!created.jobId) {
      toast.error('Job not saved yet');
      setStep(STEP_JOB);
      return;
    }
    setSubmitting(true);
    try {
      const v = await createVisitApi(created.jobId);
      setCreated((p) => ({ ...p, visitId: v.visit_id }));
      // Refresh FVO context so any lingering active flag clears on next fetch
      try { await fetchStatus?.(); } catch (_) {}
      setStep(STEP_DONE);
    } catch (err) {
      toast.error(err.message || 'Failed to schedule visit');
    } finally {
      setSubmitting(false);
    }
  };

  const handleFinish = () => {
    onComplete?.({ ...created, action: 'view_calendar' });
    onClose?.();
  };

  const handleAddAnother = () => {
    onComplete?.({ ...created, action: 'add_another' });
    onClose?.();
  };

  // ---- Renderers ----
  const renderIntro = () => (
    <div className="space-y-4">
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
          <Sparkles className="w-6 h-6 text-orange-600" />
        </div>
        <div>
          <h3 className="font-heading text-lg font-bold text-zinc-900">
            Great — now let&apos;s create your real first job.
          </h3>
          <p className="text-sm text-zinc-600 mt-1">
            We&apos;ll add the customer and job details together, then schedule it.
          </p>
        </div>
      </div>

      <ol className="space-y-2 pl-1 text-sm text-zinc-700">
        <li className="flex items-center gap-2"><User className="w-4 h-4 text-zinc-500" /> Add customer info</li>
        <li className="flex items-center gap-2"><Briefcase className="w-4 h-4 text-zinc-500" /> Add job details</li>
        <li className="flex items-center gap-2"><CalendarIcon className="w-4 h-4 text-zinc-500" /> Schedule the visit</li>
      </ol>
    </div>
  );

  const renderCustomer = () => (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-sm font-semibold text-zinc-700">
        <User className="w-4 h-4" /> Customer info
      </div>
      <div>
        <Label htmlFor="rj-name">Customer Name *</Label>
        <Input
          id="rj-name"
          value={customer.name}
          onChange={(e) => setCustomer({ ...customer, name: e.target.value })}
          placeholder="John Smith"
          className={`mt-1 h-11 border-2 ${errors.name ? 'border-red-500' : ''}`}
          data-testid="rj-customer-name"
        />
        {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label htmlFor="rj-phone">Phone</Label>
          <Input
            id="rj-phone"
            type="tel"
            value={customer.phone}
            onChange={(e) => setCustomer({ ...customer, phone: e.target.value })}
            placeholder="(555) 123-4567"
            className={`mt-1 h-11 border-2 ${errors.phone ? 'border-red-500' : ''}`}
            data-testid="rj-customer-phone"
          />
          {errors.phone && <p className="text-xs text-red-500 mt-1">{errors.phone}</p>}
        </div>
        <div>
          <Label htmlFor="rj-email">Email</Label>
          <Input
            id="rj-email"
            type="email"
            value={customer.email}
            onChange={(e) => setCustomer({ ...customer, email: e.target.value })}
            placeholder="john@example.com"
            className={`mt-1 h-11 border-2 ${errors.email ? 'border-red-500' : ''}`}
            data-testid="rj-customer-email"
          />
          {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
        </div>
      </div>
      {errors.contact && <p className="text-xs text-red-500">{errors.contact}</p>}
      <p className="text-xs text-zinc-500">* Phone or email required</p>

      <div className="pt-3 border-t border-zinc-200">
        <div className="flex items-center gap-2 mb-3 text-sm font-semibold text-zinc-700">
          <MapPin className="w-4 h-4" /> Service Address *
        </div>
        <div className="space-y-3">
          <div>
            <Label htmlFor="rj-street">Street Address *</Label>
            <Input
              id="rj-street"
              value={customer.street}
              onChange={(e) => setCustomer({ ...customer, street: e.target.value })}
              placeholder="123 Main St"
              className={`mt-1 h-11 border-2 ${errors.street ? 'border-red-500' : ''}`}
              data-testid="rj-customer-street"
            />
            {errors.street && <p className="text-xs text-red-500 mt-1">{errors.street}</p>}
          </div>
          <div>
            <Label htmlFor="rj-line2">Apt/Suite/Unit</Label>
            <Input
              id="rj-line2"
              value={customer.address_line2}
              onChange={(e) => setCustomer({ ...customer, address_line2: e.target.value })}
              placeholder="Apt 4B"
              className="mt-1 h-11 border-2"
            />
          </div>
          <div className="grid grid-cols-6 gap-3">
            <div className="col-span-3">
              <Label htmlFor="rj-city">City *</Label>
              <Input
                id="rj-city"
                value={customer.city}
                onChange={(e) => setCustomer({ ...customer, city: e.target.value })}
                placeholder="Boston"
                className={`mt-1 h-11 border-2 ${errors.city ? 'border-red-500' : ''}`}
                data-testid="rj-customer-city"
              />
            </div>
            <div className="col-span-1">
              <Label htmlFor="rj-state">State *</Label>
              <Input
                id="rj-state"
                value={customer.state}
                onChange={(e) => setCustomer({ ...customer, state: e.target.value.toUpperCase() })}
                placeholder="MA"
                maxLength={2}
                className={`mt-1 h-11 border-2 uppercase ${errors.state ? 'border-red-500' : ''}`}
                data-testid="rj-customer-state"
              />
            </div>
            <div className="col-span-2">
              <Label htmlFor="rj-zip">ZIP *</Label>
              <Input
                id="rj-zip"
                value={customer.zip}
                onChange={(e) => setCustomer({ ...customer, zip: e.target.value })}
                placeholder="02101"
                maxLength={10}
                className={`mt-1 h-11 border-2 ${errors.zip ? 'border-red-500' : ''}`}
                data-testid="rj-customer-zip"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderJob = () => (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-sm font-semibold text-zinc-700">
        <Briefcase className="w-4 h-4" /> Job details
      </div>
      <div>
        <Label htmlFor="rj-title">Job Title *</Label>
        <Input
          id="rj-title"
          value={job.title}
          onChange={(e) => setJob({ ...job, title: e.target.value })}
          placeholder="HVAC tune-up at the Smith residence"
          className={`mt-1 h-11 border-2 ${errors.title ? 'border-red-500' : ''}`}
          data-testid="rj-job-title"
        />
        {errors.title && <p className="text-xs text-red-500 mt-1">{errors.title}</p>}
      </div>
      <div>
        <Label htmlFor="rj-service">Service Type *</Label>
        <Select
          value={job.service_type}
          onValueChange={(v) => setJob({ ...job, service_type: v })}
        >
          <SelectTrigger
            id="rj-service"
            className={`mt-1 h-11 border-2 ${errors.service_type ? 'border-red-500' : ''}`}
            data-testid="rj-job-service-type"
          >
            <SelectValue placeholder="Pick a service type" />
          </SelectTrigger>
          <SelectContent>
            {(serviceTypes || []).map((st) => (
              <SelectItem key={st.slug} value={st.slug}>{st.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.service_type && <p className="text-xs text-red-500 mt-1">{errors.service_type}</p>}
      </div>
      <div>
        <Label htmlFor="rj-desc">Notes (optional)</Label>
        <Textarea
          id="rj-desc"
          value={job.description}
          onChange={(e) => setJob({ ...job, description: e.target.value })}
          placeholder="Any details your team should know"
          className="mt-1 border-2 min-h-[88px]"
          data-testid="rj-job-description"
        />
      </div>
    </div>
  );

  const renderSchedule = () => (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-sm font-semibold text-zinc-700">
        <CalendarIcon className="w-4 h-4" /> Schedule the visit
      </div>
      <p className="text-sm text-zinc-600">
        Nice — your real job is created. Now let&apos;s schedule the visit.
      </p>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label htmlFor="rj-date">Date *</Label>
          <Input
            id="rj-date"
            type="date"
            value={schedule.date}
            onChange={(e) => setSchedule({ ...schedule, date: e.target.value })}
            className={`mt-1 h-11 border-2 ${errors.date ? 'border-red-500' : ''}`}
            data-testid="rj-schedule-date"
          />
          {errors.date && <p className="text-xs text-red-500 mt-1">{errors.date}</p>}
        </div>
        <div>
          <Label htmlFor="rj-start">Start time *</Label>
          <Input
            id="rj-start"
            type="time"
            value={schedule.start_time}
            onChange={(e) => setSchedule({ ...schedule, start_time: e.target.value })}
            className={`mt-1 h-11 border-2 ${errors.start_time ? 'border-red-500' : ''}`}
            data-testid="rj-schedule-time"
          />
          {errors.start_time && <p className="text-xs text-red-500 mt-1">{errors.start_time}</p>}
        </div>
      </div>
      <div>
        <Label htmlFor="rj-dur">Duration</Label>
        <Select
          value={String(schedule.duration_minutes)}
          onValueChange={(v) => setSchedule({ ...schedule, duration_minutes: parseInt(v, 10) })}
        >
          <SelectTrigger id="rj-dur" className="mt-1 h-11 border-2" data-testid="rj-schedule-duration">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="30">30 minutes</SelectItem>
            <SelectItem value="60">1 hour</SelectItem>
            <SelectItem value="90">1.5 hours</SelectItem>
            <SelectItem value="120">2 hours</SelectItem>
            <SelectItem value="180">3 hours</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );

  const renderDone = () => (
    <div className="text-center py-2">
      <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3">
        <CheckCircle className="w-8 h-8 text-green-600" />
      </div>
      <h3 className="font-heading text-xl font-bold text-zinc-900">
        You&apos;re up and running <span aria-hidden="true">🎉</span>
      </h3>
      <p className="text-sm text-zinc-600 mt-2 max-w-sm mx-auto">
        You just scheduled your first real job. This is exactly how your business runs on Free Service Pro.
      </p>
    </div>
  );

  // ---- Footer per step ----
  const renderFooter = () => {
    if (step === STEP_INTRO) {
      return (
        <Button
          onClick={handleIntroContinue}
          className="bg-[#FF6600] hover:bg-[#CC5200] text-white font-semibold"
          data-testid="rj-intro-continue"
        >
          Continue <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      );
    }
    if (step === STEP_CUSTOMER) {
      return (
        <Button
          onClick={handleCustomerNext}
          disabled={submitting}
          className="bg-[#FF6600] hover:bg-[#CC5200] text-white font-semibold"
          data-testid="rj-customer-next"
        >
          {submitting ? 'Saving...' : <>Save & continue <ArrowRight className="w-4 h-4 ml-2" /></>}
        </Button>
      );
    }
    if (step === STEP_JOB) {
      return (
        <Button
          onClick={handleJobNext}
          disabled={submitting}
          className="bg-[#FF6600] hover:bg-[#CC5200] text-white font-semibold"
          data-testid="rj-job-next"
        >
          {submitting ? 'Saving...' : <>Save & continue <ArrowRight className="w-4 h-4 ml-2" /></>}
        </Button>
      );
    }
    if (step === STEP_SCHEDULE) {
      return (
        <Button
          onClick={handleScheduleNext}
          disabled={submitting}
          className="bg-[#FF6600] hover:bg-[#CC5200] text-white font-semibold"
          data-testid="rj-schedule-confirm"
        >
          {submitting ? 'Scheduling...' : 'Schedule visit'}
        </Button>
      );
    }
    if (step === STEP_DONE) {
      return (
        <div className="flex flex-col-reverse sm:flex-row gap-2 w-full">
          <Button
            onClick={handleAddAnother}
            variant="outline"
            className="flex-1 sm:flex-none border-2 border-zinc-300 bg-white text-zinc-800 hover:bg-zinc-100 hover:text-zinc-900 hover:border-zinc-400 focus:bg-zinc-100 focus:text-zinc-900 active:bg-zinc-200"
            data-testid="rj-done-add-another"
          >
            Add Another Job
          </Button>
          <Button
            onClick={handleFinish}
            className="flex-1 sm:flex-none bg-[#FF6600] hover:bg-[#CC5200] text-white font-semibold"
            data-testid="rj-done-view-calendar"
          >
            View My Calendar
          </Button>
        </div>
      );
    }
    return null;
  };

  // Modal cannot be dismissed by user during the guided flow except at intro/done.
  const dismissible = step === STEP_INTRO || step === STEP_DONE;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open && dismissible) onClose?.(); }}>
      <DialogContent
        className="sm:max-w-lg z-[80]"
        data-testid="real-first-job-flow"
        // Defensively ignore closes from outside-click / escape during steps 1-3
        onPointerDownOutside={(e) => { if (!dismissible) e.preventDefault(); }}
        onEscapeKeyDown={(e) => { if (!dismissible) e.preventDefault(); }}
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-xl uppercase">{headerTitle}</DialogTitle>
        </DialogHeader>

        <DialogBody>
          {step !== STEP_INTRO && step !== STEP_DONE && <StepDots current={step} />}
          {step === STEP_INTRO && renderIntro()}
          {step === STEP_CUSTOMER && renderCustomer()}
          {step === STEP_JOB && renderJob()}
          {step === STEP_SCHEDULE && renderSchedule()}
          {step === STEP_DONE && renderDone()}
        </DialogBody>

        <DialogFooter>{renderFooter()}</DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
